import { formatISO, sub } from 'date-fns'

import { useGetPluginPingsStatusQuery } from '../../../app/apiSliceCoreLogicApi'
import { CHART_NEUTRAL_HSLA, CHART_PRIMARY_HSLA } from '../../../app/theme'
import { useDashboard } from '../../../common/hooks'
import {
  GranularityEnum,
  OrganizationLevelEnum,
  type PluginPingsPayload,
} from '../../../common/types/monitoring'
import { AdminMainPathEnum } from '../../../common/types/routing'
import { fractionToPercentage } from '../../../common/utils/numberFormatting'
import StatusCard from './StatusCard'

const AgentStatus = () => {
  const { company } = useDashboard()

  const { data: rawData, isLoading } = useGetPluginPingsStatusQuery({
    start_timestamp: formatISO(sub(new Date().setHours(0, 0, 0, 0), { days: 6 })),
    end_timestamp: formatISO(new Date().setHours(0, 0, 0, 0)),
    granularity: GranularityEnum.DAY,
    organization_level: OrganizationLevelEnum.COMPANY,
  })

  const installationTarget = company.agentInstallationTarget
  const currentComputerConnectedCount =
    rawData?.reduce(
      (max: number, ping: PluginPingsPayload[number]) => Math.max(max, ping.computer_count),
      0,
    ) ?? 0

  /** Cap slices at target so the doughnut stays 0–100% when usage exceeds capacity. */
  const chartConnected =
    installationTarget > 0
      ? Math.min(currentComputerConnectedCount, installationTarget)
      : currentComputerConnectedCount
  const chartRemaining =
    installationTarget > 0 ? Math.max(0, installationTarget - currentComputerConnectedCount) : 0

  const chartData =
    chartConnected === 0 && chartRemaining === 0
      ? {
          labels: ['No connected computers'],
          datasets: [
            {
              data: [1],
              backgroundColor: [CHART_NEUTRAL_HSLA],
            },
          ],
        }
      : {
          labels: ['Connected computers', 'Computers remaining'],
          datasets: [
            {
              data: [chartConnected, chartRemaining],
              backgroundColor: [CHART_PRIMARY_HSLA, CHART_NEUTRAL_HSLA],
            },
          ],
        }

  const usageFraction =
    installationTarget > 0
      ? Math.min(1, currentComputerConnectedCount / installationTarget)
      : currentComputerConnectedCount > 0
        ? 1
        : 0

  return (
    <StatusCard
      chartData={chartData}
      titleProps={{
        label: 'Connected computers',
        value: fractionToPercentage(usageFraction),
      }}
      descriptionProps={{
        label: 'Last 7 days',
        value: `${currentComputerConnectedCount}/${installationTarget}`,
      }}
      navProps={{
        label: 'Download installer',
        link: AdminMainPathEnum.INSTALLATION_CENTER,
      }}
      isLoading={isLoading}
    />
  )
}

export default AgentStatus

